import deburr from 'lodash/deburr';

export type ChecklistOption = { value: string, label: string };
export type ChecklistGroup = { value: string, label: string, options: ChecklistItem[] };
export type ChecklistItem = ChecklistOption | ChecklistGroup;

export type ChecklistRow =
  | {
    kind: 'group', value: string, label: string, depth: number,
    picked: number, whole: boolean, open: boolean, collapsible: boolean, foundByName: boolean,
  }
  | { kind: 'option', value: string, label: string, depth: number, checked: boolean };

export type ChecklistView = {
  selected: ReadonlySet<string>,
  expanded: ReadonlySet<string>,
  query: string,
  showOnly: ReadonlySet<string> | null,
  matches: (option: ChecklistOption, query: string) => boolean,
  pickableGroups: boolean,
};

export const isGroup = (item: ChecklistItem): item is ChecklistGroup => 'options' in item;

// deburr folds letters like Ø and Æ; NFD first splits the accented letters outside its Latin-1 range.
const foldForSearch = (text: string) => deburr(text.normalize('NFD')).toLowerCase();

export const matchesLabel = (option: ChecklistOption, query: string) =>
  foldForSearch(option.label).includes(foldForSearch(query.trim()));

export const matchesLabelOrValue = (option: ChecklistOption, query: string) =>
  matchesLabel(option, query) || foldForSearch(option.value).includes(foldForSearch(query.trim()));

export const hasMatch = (
  items: readonly ChecklistItem[],
  query: string,
  matches: (option: ChecklistOption, query: string) => boolean,
): boolean => items.some(item => matches(item, query)
  || (isGroup(item) && hasMatch(item.options, query, matches)));

// A value counts too: a parent that keeps created names as values would otherwise get one twice.
export const isNameTaken = (items: readonly ChecklistItem[], name: string): boolean => {
  const wanted = foldForSearch(name.trim());
  return items.some(item => foldForSearch(item.label) === wanted
    || foldForSearch(item.value) === wanted
    || (isGroup(item) && isNameTaken(item.options, name)));
};

// A search or "Visa bara valda" lists only what it lets through, under sections held open.
export const checklistRows = (items: readonly ChecklistItem[], view: ChecklistView): ChecklistRow[] => {
  const { selected, expanded, showOnly, matches, pickableGroups } = view;
  const query = view.query.trim();
  const filtering = query !== '' || showOnly !== null;

  const walk = (list: readonly ChecklistItem[], depth: number, heldByAncestor: boolean, ancestorInSnapshot: boolean) => {
    const inSnapshot = (value: string) => !showOnly || ancestorInSnapshot || showOnly.has(value);
    const rows: ChecklistRow[] = [];
    let picked = 0;
    let total = 0;
    let ticked = 0;
    list.forEach(item => {
      if (!isGroup(item)) {
        const checked = heldByAncestor || selected.has(item.value);
        total += 1;
        if (checked) ticked += 1;
        if (selected.has(item.value)) picked += 1;
        const listed = inSnapshot(item.value) && (!query || matches(item, query));
        if (listed) rows.push({ kind: 'option', value: item.value, label: item.label, depth, checked });
        return;
      }

      const held = selected.has(item.value);
      const inner = walk(
        item.options,
        depth + 1,
        heldByAncestor || held,
        ancestorInSnapshot || (showOnly?.has(item.value) ?? false),
      );
      total += inner.total;
      ticked += inner.ticked;
      const whole = inner.total > 0 && inner.ticked === inner.total;
      picked += held || whole ? 1 : inner.picked;
      const foundByName = pickableGroups && query !== '' && inSnapshot(item.value) && matches(item, query);
      if (filtering && !foundByName && inner.rows.length === 0) return;

      const open = filtering || expanded.has(item.value);
      rows.push({
        kind: 'group',
        value: item.value,
        label: item.label,
        depth,
        picked: inner.picked,
        whole,
        open,
        collapsible: !filtering,
        foundByName,
      });
      if (open) rows.push(...inner.rows);
    });
    return { rows, picked, total, ticked };
  };

  return walk(items, 0, false, false).rows;
};

export const setOption = (value: readonly string[], option: string, checked: boolean): string[] =>
  (checked ? [...value, option] : value.filter(picked => picked !== option));

// Only the node and the sections above it change; a section folded or opened up keeps its place.
export const setInTree = (
  items: readonly ChecklistItem[],
  value: readonly string[],
  node: string,
  picked: boolean,
): string[] => {
  const nodeOf = new Map<string, ChecklistItem>();
  const parentOf = new Map<string, ChecklistGroup>();
  const index = (list: readonly ChecklistItem[], parent?: ChecklistGroup) => list.forEach(item => {
    nodeOf.set(item.value, item);
    if (parent) parentOf.set(item.value, parent);
    if (isGroup(item)) index(item.options, item);
  });
  index(items);

  const target = nodeOf.get(node);
  if (!target) return [...value];
  const descendants = (item: ChecklistItem): string[] => (isGroup(item)
    ? item.options.flatMap(child => [child.value, ...descendants(child)])
    : []);
  const beneath = new Set(descendants(target));
  const ancestors: ChecklistGroup[] = [];
  for (let parent = parentOf.get(node); parent; parent = parentOf.get(parent.value)) ancestors.push(parent);
  const heldAbove = ancestors.filter(ancestor => value.includes(ancestor.value)).map(ancestor => ancestor.value);

  if (picked) {
    if (heldAbove.length || value.includes(node)) return [...value];
    let result = [...value.filter(other => !beneath.has(other)), node];
    for (let level = 0; level < ancestors.length; level += 1) {
      const siblings = ancestors[level].options.map(child => child.value);
      if (!siblings.every(sibling => result.includes(sibling))) break;
      const at = result.findIndex(other => siblings.includes(other));
      result = result.filter(other => !siblings.includes(other));
      result.splice(at, 0, ancestors[level].value);
    }
    return result;
  }

  const result = value.filter(other => other !== node && !beneath.has(other));
  if (!heldAbove.length) return result;
  // Saved lists can hold a section next to one inside it, so every held section above opens at once.
  const outermost = ancestors.findIndex(ancestor => ancestor.value === heldAbove[heldAbove.length - 1]);
  const opened: string[] = [];
  for (let level = outermost; level >= 0; level -= 1) {
    const onPath = level === 0 ? node : ancestors[level - 1].value;
    ancestors[level].options.forEach(child => {
      if (child.value !== onPath && !result.includes(child.value)) opened.push(child.value);
    });
  }
  const before = result.slice(0, result.indexOf(ancestors[outermost].value));
  const kept = result.filter(other => !heldAbove.includes(other));
  kept.splice(before.filter(other => !heldAbove.includes(other)).length, 0, ...opened);
  return kept;
};
